/**
 * courseValidationService.js - Sprint 3
 * 
 * Servicio de validación completa para cursos grupales.
 * Detecta errores críticos (que impiden publicación) y warnings (recomendaciones).
 * 
 * @see PLAN_GROUPCOURSE_BUILDER.md - Sprint 3, Día 15
 */

/**
 * Valida un curso grupal completo
 * @param {Object} course - Objeto del curso con todas sus semanas
 * @returns {Object} Resultado de validación con errores, warnings y estadísticas
 */
export const validateGroupCourse = (course) => {
  const errors = [];
  const warnings = [];

  // ===== 1. VALIDAR METADATA BÁSICA =====
  if (!course.title || course.title.trim() === '') {
    errors.push({ 
      type: 'metadata', 
      section: 'Información General',
      field: 'Título',
      message: 'El título del curso es requerido' 
    });
  }

  if (!course.description || course.description.trim() === '') {
    warnings.push({ 
      type: 'metadata',
      section: 'Información General',
      field: 'Descripción',
      message: 'Se recomienda agregar una descripción del curso' 
    });
  }

  if (!course.category || course.category.trim() === '') {
    warnings.push({ 
      type: 'metadata',
      section: 'Información General',
      field: 'Categoría',
      message: 'Se recomienda especificar la categoría del curso' 
    });
  }

  // ===== 2. VALIDAR CONFIGURACIÓN GRUPAL =====
  if (!course.groupCourseConfig) {
    errors.push({ 
      type: 'config', 
      section: 'Configuración Grupal',
      message: 'La configuración grupal no está definida' 
    });
  } else {
    const config = course.groupCourseConfig;

    // Validar tamaños de grupo
    if (!config.minGroupSize || config.minGroupSize < 2) {
      errors.push({
        type: 'config',
        section: 'Configuración Grupal',
        field: 'Tamaño mínimo de grupo',
        message: 'El tamaño mínimo de grupo debe ser al menos 2'
      });
    }

    if (!config.maxGroupSize || config.maxGroupSize < config.minGroupSize) {
      errors.push({
        type: 'config',
        section: 'Configuración Grupal',
        field: 'Tamaño máximo de grupo',
        message: 'El tamaño máximo debe ser mayor o igual al mínimo'
      });
    }

    // Validar pesos de evaluación
    if (config.weights) {
      const weights = config.weights;
      const totalWeight = 
        (weights.deliverable || 0) + 
        (weights.crossFeedback || 0) + 
        (weights.aiCoachFeedback || 0);
      
      if (totalWeight !== 100) {
        errors.push({ 
          type: 'config', 
          section: 'Configuración Grupal',
          field: 'Distribución de Pesos',
          message: `La suma de pesos debe ser exactamente 100% (actual: ${totalWeight}%)`,
          details: {
            deliverable: weights.deliverable || 0,
            crossFeedback: weights.crossFeedback || 0,
            aiCoachFeedback: weights.aiCoachFeedback || 0,
            total: totalWeight
          }
        });
      }

      // Verificar que cada peso esté entre 0 y 100
      if (weights.deliverable < 0 || weights.deliverable > 100) {
        errors.push({
          type: 'config',
          section: 'Configuración Grupal',
          field: 'Peso Entregable',
          message: `El peso del entregable debe estar entre 0 y 100 (actual: ${weights.deliverable})`
        });
      }
      if (weights.crossFeedback < 0 || weights.crossFeedback > 100) {
        errors.push({
          type: 'config',
          section: 'Configuración Grupal',
          field: 'Peso Cross-Feedback',
          message: `El peso de cross-feedback debe estar entre 0 y 100 (actual: ${weights.crossFeedback})`
        });
      }
      if (weights.aiCoachFeedback < 0 || weights.aiCoachFeedback > 100) {
        errors.push({
          type: 'config',
          section: 'Configuración Grupal',
          field: 'Peso Coach IA',
          message: `El peso del coach IA debe estar entre 0 y 100 (actual: ${weights.aiCoachFeedback})`
        });
      }
    } else {
      errors.push({
        type: 'config',
        section: 'Configuración Grupal',
        field: 'Pesos de Evaluación',
        message: 'No se han definido los pesos de evaluación'
      });
    }
  }

  // ===== 3. VALIDAR SEMANAS =====
  if (!course.weeks || course.weeks.length === 0) {
    errors.push({
      type: 'structure',
      section: 'Estructura del Curso',
      message: 'El curso debe tener al menos una semana definida'
    });
  } else {
    course.weeks.forEach((week, index) => {
      const weekNum = week.weekNumber || index + 1;
      
      // B1: Validar recursos (WARNING si no hay)
      if (!week.b1_resources || week.b1_resources.length === 0) {
        warnings.push({ 
          type: 'content',
          week: weekNum, 
          block: 'B1',
          section: `Semana ${weekNum}`,
          message: 'No tiene recursos asignados. Se recomienda agregar al menos un recurso de aprendizaje.' 
        });
      }
      
      // B2: Validar template de ejecución (ERROR si no hay campos)
      if (!week.b2_execution_template) {
        errors.push({ 
          type: 'content',
          week: weekNum,
          block: 'B2',
          section: `Semana ${weekNum}`,
          message: 'El template de ejecución no está definido (OBLIGATORIO)'
        });
      } else if (!week.b2_execution_template.fields || week.b2_execution_template.fields.length === 0) {
        errors.push({ 
          type: 'content',
          week: weekNum,
          block: 'B2',
          section: `Semana ${weekNum}`,
          message: 'El template de ejecución debe tener al menos un campo definido (OBLIGATORIO)'
        });
      }
      
      // B3: Validar criterios de auditoría (pesos deben sumar 100%)
      if (week.b3_criteria && week.b3_criteria.length > 0) {
        const totalB3Weight = week.b3_criteria.reduce((sum, c) => sum + (c.weight || 0), 0);
        if (Math.abs(totalB3Weight - 100) > 0.01) { // Tolerancia de 0.01 para decimales
          errors.push({
            type: 'weights',
            week: weekNum,
            block: 'B3',
            section: `Semana ${weekNum}`,
            message: `Los pesos de los criterios de auditoría deben sumar exactamente 100% (actual: ${totalB3Weight.toFixed(2)}%)`,
            details: {
              criteria: week.b3_criteria.map(c => ({ name: c.name, weight: c.weight })),
              total: totalB3Weight
            }
          });
        }

        // Verificar que cada criterio tenga nombre y peso
        week.b3_criteria.forEach((criterion, idx) => {
          if (!criterion.name || criterion.name.trim() === '') {
            errors.push({
              type: 'content',
              week: weekNum,
              block: 'B3',
              section: `Semana ${weekNum}`,
              message: `Criterio de auditoría #${idx + 1} no tiene nombre`
            });
          }
          if (criterion.weight === undefined || criterion.weight === null || criterion.weight < 0) {
            errors.push({
              type: 'content',
              week: weekNum,
              block: 'B3',
              section: `Semana ${weekNum}`,
              message: `Criterio "${criterion.name || 'Sin nombre'}" no tiene un peso válido`
            });
          }
        });
      }
      
      // B4: Validar configuración del coach (solo warnings)
      if (week.b4_coach_config) {
        if (week.b4_coach_config.enabled && (!week.b4_coach_config.focusAreas || week.b4_coach_config.focusAreas.length === 0)) {
          warnings.push({
            type: 'config',
            week: weekNum,
            block: 'B4',
            section: `Semana ${weekNum}`,
            message: 'El Coach IA está habilitado pero no tiene áreas de enfoque definidas. Se recomienda seleccionar al menos una.'
          });
        }
      }

      // B5: Validar template de formalización (solo warnings)
      if (week.b5_formalization_template) {
        const template = week.b5_formalization_template;
        if (template.format === 'structured' && (!template.sections || template.sections.length === 0)) {
          warnings.push({
            type: 'content',
            week: weekNum,
            block: 'B5',
            section: `Semana ${weekNum}`,
            message: 'El template de formalización está en formato estructurado pero no tiene secciones definidas'
          });
        }
        if (template.format === 'markdown' && (!template.markdownContent || template.markdownContent.trim() === '')) {
          warnings.push({
            type: 'content',
            week: weekNum,
            block: 'B5',
            section: `Semana ${weekNum}`,
            message: 'El template de formalización en Markdown está vacío'
          });
        }
        if (template.format === 'json' && (!template.jsonSchema || template.jsonSchema.trim() === '')) {
          warnings.push({
            type: 'content',
            week: weekNum,
            block: 'B5',
            section: `Semana ${weekNum}`,
            message: 'El template de formalización en JSON está vacío'
          });
        }
      }
      
      // B6: Validar criterios de cross-feedback (pesos deben sumar 100%)
      if (week.b6_criteria && week.b6_criteria.length > 0) {
        const totalB6Weight = week.b6_criteria.reduce((sum, c) => sum + (c.weight || 0), 0);
        if (Math.abs(totalB6Weight - 100) > 0.01) {
          errors.push({
            type: 'weights',
            week: weekNum,
            block: 'B6',
            section: `Semana ${weekNum}`,
            message: `Los pesos de los criterios de cross-feedback deben sumar exactamente 100% (actual: ${totalB6Weight.toFixed(2)}%)`,
            details: {
              criteria: week.b6_criteria.map(c => ({ name: c.name, weight: c.weight })),
              total: totalB6Weight
            }
          });
        }

        // Verificar que cada criterio tenga nombre y peso
        week.b6_criteria.forEach((criterion, idx) => {
          if (!criterion.name || criterion.name.trim() === '') {
            errors.push({
              type: 'content',
              week: weekNum,
              block: 'B6',
              section: `Semana ${weekNum}`,
              message: `Criterio de cross-feedback #${idx + 1} no tiene nombre`
            });
          }
          if (criterion.weight === undefined || criterion.weight === null || criterion.weight < 0) {
            errors.push({
              type: 'content',
              week: weekNum,
              block: 'B6',
              section: `Semana ${weekNum}`,
              message: `Criterio "${criterion.name || 'Sin nombre'}" no tiene un peso válido`
            });
          }
        });
      }
    });
  }

  // ===== 4. CALCULAR ESTADÍSTICAS =====
  const totalWeeks = course.weeks ? course.weeks.length : 0;
  const completeWeeks = course.weeks ? course.weeks.filter(w => {
    const hasResources = w.b1_resources && w.b1_resources.length > 0;
    const hasTemplate = w.b2_execution_template && w.b2_execution_template.fields && w.b2_execution_template.fields.length > 0;
    const hasB3Criteria = w.b3_criteria && w.b3_criteria.length > 0;
    return hasResources && hasTemplate && hasB3Criteria;
  }).length : 0;

  const completenessPercentage = totalWeeks > 0 
    ? Math.round((completeWeeks / totalWeeks) * 100) 
    : 0;

  return {
    valid: errors.length === 0,
    canPublish: errors.length === 0, // Solo se puede publicar si no hay errores
    errors,
    warnings,
    stats: {
      totalWeeks,
      completeWeeks,
      completenessPercentage,
      totalErrors: errors.length,
      totalWarnings: warnings.length,
      errorsByType: {
        metadata: errors.filter(e => e.type === 'metadata').length,
        config: errors.filter(e => e.type === 'config').length,
        content: errors.filter(e => e.type === 'content').length,
        weights: errors.filter(e => e.type === 'weights').length,
        structure: errors.filter(e => e.type === 'structure').length
      },
      warningsByBlock: {
        B1: warnings.filter(w => w.block === 'B1').length,
        B2: warnings.filter(w => w.block === 'B2').length,
        B3: warnings.filter(w => w.block === 'B3').length,
        B4: warnings.filter(w => w.block === 'B4').length,
        B5: warnings.filter(w => w.block === 'B5').length,
        B6: warnings.filter(w => w.block === 'B6').length
      }
    }
  };
};

/**
 * Valida solo una semana específica
 * @param {Object} week - Datos de la semana
 * @param {number} weekNumber - Número de la semana
 * @returns {Object} Resultado de validación para la semana
 */
export const validateWeek = (week, weekNumber) => {
  const errors = [];
  const warnings = [];

  // Reutilizar la lógica de validación de semana del servicio principal
  const mockCourse = {
    weeks: [{ ...week, weekNumber }]
  };

  const fullValidation = validateGroupCourse(mockCourse);
  
  return {
    valid: fullValidation.errors.filter(e => e.week === weekNumber).length === 0,
    errors: fullValidation.errors.filter(e => e.week === weekNumber),
    warnings: fullValidation.warnings.filter(w => w.week === weekNumber)
  };
};

/**
 * Genera un reporte legible de validación
 * @param {Object} validationResult - Resultado de validateGroupCourse
 * @returns {string} Reporte en texto
 */
export const generateValidationReport = (validationResult) => {
  let report = '=== REPORTE DE VALIDACIÓN ===\n\n';
  
  report += `Estado: ${validationResult.valid ? '✓ VÁLIDO' : '✗ NO VÁLIDO'}\n`;
  report += `Se puede publicar: ${validationResult.canPublish ? 'SÍ' : 'NO'}\n\n`;
  
  report += `Estadísticas:\n`;
  report += `- Total de semanas: ${validationResult.stats.totalWeeks}\n`;
  report += `- Semanas completas: ${validationResult.stats.completeWeeks} (${validationResult.stats.completenessPercentage}%)\n`;
  report += `- Errores críticos: ${validationResult.stats.totalErrors}\n`;
  report += `- Advertencias: ${validationResult.stats.totalWarnings}\n\n`;
  
  if (validationResult.errors.length > 0) {
    report += '❌ ERRORES CRÍTICOS:\n';
    validationResult.errors.forEach((error, idx) => {
      report += `${idx + 1}. [${error.section}] ${error.message}\n`;
      if (error.details) {
        report += `   Detalles: ${JSON.stringify(error.details, null, 2)}\n`;
      }
    });
    report += '\n';
  }
  
  if (validationResult.warnings.length > 0) {
    report += '⚠️ ADVERTENCIAS:\n';
    validationResult.warnings.forEach((warning, idx) => {
      report += `${idx + 1}. [${warning.section}] ${warning.message}\n`;
    });
  }
  
  return report;
};
